/* ============================================================
   PRO STUDIO → CTA BEG KUNING
   ============================================================
   "Beg kuning" = pautan produk pada video TikTok. CTA di sini ialah
   teks pada BUTANG pautan itu — bukan ayat penutup caption. Petak ini
   dinamakan "CTA & Hashtag" dalam menu; bahagian hashtag/caption
   datang kemudian (keputusan user 2026-09-04: CTA dahulu).

   ⭐ RUPA SAHAJA — prop-driven. Tiada Firestore, tiada localStorage di
      dalam fail ini. Corak sama ProStudioKonfigurV39: satu takrifan
      rupa, dan pemanggil yang memberi data + tindakan. Sebabnya bukan
      gaya — ia membolehkan skrin ini dipakai dalam mod pelan (data
      contoh) tanpa menyentuh akaun pelanggan.

   ⚠️ Pelanggan TIDAK memilih "guna yang mana" di sini. Pilihan itu
      dibuat pada setiap larian, di Control Panel → tab Schedule, dan
      disimpan per-browser. Sengaja: PRO Studio = urus koleksi (milik
      akaun); Control Panel = keputusan larian (milik browser ini).
   ============================================================ */

import { useState } from 'react';
import { Plus, Trash2, Check, X, Pencil } from 'lucide-react';

import { Card, CardBody, Btn, IconBtn, Badge } from '../design/ui';
import { CTA_MAX, CTA_LALAI } from '../utils/ctaStore';

export default function ProStudioCtaSection({
    senarai = [],
    sudahMuat = false,
    readOnly = false,
    onSimpan,          // ({id, teks}) => Promise
    onPadam,           // (id) => Promise
    t,
}) {
    const [teksBaru, setTeksBaru] = useState('');
    const [sedangEdit, setSedangEdit] = useState(null);   // id
    const [teksEdit, setTeksEdit] = useState('');
    const [sibuk, setSibuk] = useState(false);
    const [ralat, setRalat] = useState('');

    /* Terjemahan dengan ayat lalai. ⚠️ `t(kunci, vars)` — parameter kedua
       ialah pemboleh ubah untuk disisip, BUKAN nilai lalai. Bila kunci
       tiada, ia memulangkan nama kunci itu sendiri; jadi kita semak dan
       ganti. Tanpa ini, kunci yang tertinggal akan tercetak sebagai
       `prostudio.cta.add` pada skrin pelanggan. */
    const T = (kunci, lalai) => {
        if (!t) return lalai;
        const hasil = t(kunci);
        return (!hasil || hasil === kunci) ? lalai : hasil;
    };

    const bolehTambah = teksBaru.trim().length > 0 && !sibuk && !readOnly;

    const tambah = async () => {
        if (!bolehTambah) return;
        setSibuk(true); setRalat('');
        try {
            await onSimpan?.({ teks: teksBaru });
            setTeksBaru('');
        } catch (e) {
            setRalat(e?.message || 'Gagal menyimpan');
        } finally { setSibuk(false); }
    };

    const simpanEdit = async (id) => {
        if (!teksEdit.trim()) return;
        setSibuk(true); setRalat('');
        try {
            await onSimpan?.({ id, teks: teksEdit });
            setSedangEdit(null); setTeksEdit('');
        } catch (e) {
            setRalat(e?.message || 'Gagal menyimpan');
        } finally { setSibuk(false); }
    };

    const kotak = {
        width: '100%', boxSizing: 'border-box', height: 38, padding: '0 12px',
        borderRadius: 9, border: '1px solid var(--s-line-strong)',
        background: 'var(--s-fill)', color: 'var(--s-ink)',
        font: 'inherit', fontSize: 14,
    };

    return (
        <>
            <Card>
                <CardBody>
                    <p style={{ margin: '0 0 4px', fontSize: 13.5, fontWeight: 600, color: 'var(--s-ink)' }}>
                        {T('prostudio.cta.addTitle', 'Tambah CTA')}
                    </p>
                    <p className="s-dim" style={{ margin: '0 0 12px', fontSize: 12.5 }}>
                        {T('prostudio.cta.addHint',
                            'Teks ini muncul pada butang pautan produk (beg kuning) dalam video TikTok anda.')}
                    </p>

                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                        <div style={{ flex: '1 1 240px', minWidth: 200 }}>
                            <input
                                style={kotak}
                                value={teksBaru}
                                maxLength={CTA_MAX}
                                disabled={readOnly || sibuk}
                                placeholder={CTA_LALAI}
                                onChange={(e) => setTeksBaru(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') tambah(); }}
                            />
                            {/* Kiraan aksara sentiasa kelihatan, bukan hanya bila had
                                dilanggar — TikTok memotong teks yang terlalu panjang,
                                dan pelanggan patut nampak hadnya sebelum menaip habis. */}
                            <p className="s-dim" style={{ margin: '6px 0 0', fontSize: 11.5 }}>
                                {teksBaru.length} / {CTA_MAX}
                            </p>
                        </div>
                        <Btn variant="primary" icon={<Plus size={15} strokeWidth={2} />}
                            disabled={!bolehTambah} onClick={tambah}>
                            {T('prostudio.cta.add', 'Tambah')}
                        </Btn>
                    </div>

                    {ralat && (
                        <p style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--s-stop)' }}>{ralat}</p>
                    )}
                </CardBody>
            </Card>

            <div style={{ height: 16 }} />

            {!sudahMuat ? (
                <p className="s-dim" style={{ fontSize: 13 }}>{T('prostudio.cta.loading', 'Memuatkan…')}</p>
            ) : (
                <Card>
                    <CardBody style={{ padding: 0 }}>
                        {/* ⭐ BARIS LALAI — sentiasa ada, tidak disimpan di mana-mana,
                            tidak boleh disunting atau dipadam.

                            Sebabnya (arahan user 2026-09-04): pelanggan yang tidak
                            perasan ciri ini wujud MESTI tetap dapat hasil yang betul.
                            Sebelum ini teks itu tersembunyi dalam kod — skrin memapar
                            "Belum ada CTA" dan pelanggan tidak tahu butangnya sebenarnya
                            sudah bertulis sesuatu. Sekarang ia kelihatan, jadi pelanggan
                            faham keadaan lalai dan boleh memilih untuk menggantikannya.

                            ⛔ JANGAN jadikan ia doc Firestore sebenar — itu bermakna
                               menulis ke 1,700 akaun, dan menimbulkan soalan "apa jadi
                               kalau dipadam". Yang sebenarnya dipakai ialah jatuh-balik
                               dalam skrip automation; baris ini cerminan kepadanya. */}
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '12px 16px', background: 'var(--s-fill)',
                        }}>
                            <span style={{ flex: 1, fontSize: 14, color: 'var(--s-ink)' }}>{CTA_LALAI}</span>
                            <Badge tone="neutral">{T('prostudio.cta.defaultBadge', 'Lalai')}</Badge>
                        </div>
                        <p className="s-dim" style={{
                            margin: 0, padding: '0 16px 12px', fontSize: 12,
                            background: 'var(--s-fill)', lineHeight: 1.55,
                        }}>
                            {T('prostudio.cta.defaultHint',
                                'Digunakan secara automatik selagi anda tidak memilih CTA lain. Tidak boleh dipadam.')}
                        </p>

                        {senarai.map((c) => (
                            <div key={c.id} style={{
                                display: 'flex', alignItems: 'center', gap: 10,
                                padding: '12px 16px',
                                borderTop: '1px solid var(--s-line)',
                            }}>
                                {sedangEdit === c.id ? (
                                    <>
                                        <input
                                            style={{ ...kotak, flex: 1 }}
                                            value={teksEdit}
                                            maxLength={CTA_MAX}
                                            autoFocus
                                            onChange={(e) => setTeksEdit(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') simpanEdit(c.id);
                                                if (e.key === 'Escape') { setSedangEdit(null); setTeksEdit(''); }
                                            }}
                                        />
                                        <IconBtn icon={<Check size={15} strokeWidth={2} />}
                                            label={T('prostudio.cta.save', 'Simpan')}
                                            disabled={sibuk || !teksEdit.trim()}
                                            onClick={() => simpanEdit(c.id)} />
                                        <IconBtn icon={<X size={15} strokeWidth={2} />}
                                            label={T('prostudio.cta.cancel', 'Batal')}
                                            onClick={() => { setSedangEdit(null); setTeksEdit(''); }} />
                                    </>
                                ) : (
                                    <>
                                        <span style={{
                                            flex: 1, fontSize: 14, color: 'var(--s-ink)',
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                        }} title={c.teks}>{c.teks}</span>
                                        <Badge tone="neutral">{String(c.teks || '').length}/{CTA_MAX}</Badge>
                                        {!readOnly && (
                                            <>
                                                <IconBtn icon={<Pencil size={15} strokeWidth={1.9} />}
                                                    label={T('prostudio.cta.edit', 'Edit')}
                                                    onClick={() => { setSedangEdit(c.id); setTeksEdit(c.teks || ''); }} />
                                                <IconBtn icon={<Trash2 size={15} strokeWidth={1.9} />}
                                                    label={T('prostudio.cta.delete', 'Padam')}
                                                    onClick={() => onPadam?.(c.id)} />
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                        ))}
                    </CardBody>
                </Card>
            )}

            {/* Ayat penutup — memberitahu pelanggan DI MANA pilihan dibuat.
                Tanpa ia, skrin ini membaca seolah-olah menambah CTA sudah
                cukup untuk ia dipakai; sedangkan ia belum. */}
            <p className="s-dim" style={{ marginTop: 18, fontSize: 12.5, lineHeight: 1.6 }}>
                {T('prostudio.cta.footNote',
                    'Untuk memilih CTA mana yang dipakai semasa Run: buka Workspace → panel kanan → tab Schedule. Pilihan itu disimpan pada browser ini sahaja, jadi setiap browser boleh guna CTA berbeza.')}
            </p>
        </>
    );
}
